import { type Ref, computed, inject, onMounted, ref } from 'vue';
import { useTieredDiscounts } from '@bc/brands';
import { useFundedDiscounts, FUNDED_DISCOUNTS_KEY, type FundedDiscountsContext } from '@bc/loyalty';
import { resolveDiscountState, type DiscountDisplay } from '@bc/shared/services/discount-state';
import type { AnkorstoreInternalTierResource } from '@ankorstore/api-internal/data-contracts';

export interface UseDiscountDisplayOptions {
  brandUuid: Ref<string | undefined>;
  /** Guaranteed brand-funded discount % (product-level `discount_rate`). */
  discountPercentage: Ref<number | undefined>;
  /** Whether there is a concrete discounted price to strike through for `discountPercentage`. */
  hasDiscountedPrice: Ref<boolean | undefined>;
  hideVolumeDiscount?: Ref<boolean | undefined>;
}

/**
 * Resolves a product's discount display state from the tiered-discount and funded-discount
 * composables, gated so the result settles once rather than flickering as each source resolves.
 * Shared by every surface that shows a discount label, so they read one state machine
 * (`resolveDiscountState`) and cannot silently diverge from each other.
 */
export function useDiscountDisplay(options: UseDiscountDisplayOptions) {
  const { ensureLoaded, hasVolumeBasedDiscount, getMaxDiscountPercentage, getDiscountTiers } = useTieredDiscounts();

  const injectedFundedDiscounts = inject<FundedDiscountsContext | undefined>(FUNDED_DISCOUNTS_KEY, undefined);
  const ownFundedDiscounts = injectedFundedDiscounts ? null : useFundedDiscounts();

  const isLoaded = ref(false);

  const fundedPercentage = computed(() => {
    const brandUuid = options.brandUuid.value;
    if (!brandUuid) {
      return undefined;
    }

    const offer = injectedFundedDiscounts
      ? injectedFundedDiscounts.getOffer(brandUuid)
      : ownFundedDiscounts?.getOffer(brandUuid);

    return offer?.attributes.percentage;
  });

  const state = computed<DiscountDisplay>(() => {
    const brandUuid = options.brandUuid.value;
    const hasVolumeDiscount =
      !options.hideVolumeDiscount?.value && !!brandUuid && hasVolumeBasedDiscount(brandUuid);

    return resolveDiscountState({
      guaranteedPercentage: options.hasDiscountedPrice.value ? options.discountPercentage.value ?? 0 : 0,
      hasVolumeDiscount,
      volumeMaxPercentage: brandUuid ? getMaxDiscountPercentage(brandUuid) : 0,
      fundedPercentage: fundedPercentage.value,
    });
  });

  const tiers = computed<AnkorstoreInternalTierResource[]>(() =>
    options.brandUuid.value ? getDiscountTiers(options.brandUuid.value) : []
  );

  onMounted(async () => {
    const promises: Promise<unknown>[] = [ensureLoaded()];

    if (!injectedFundedDiscounts && ownFundedDiscounts && options.brandUuid.value) {
      promises.push(ownFundedDiscounts.ensureLoaded([options.brandUuid.value]));
    }

    await Promise.allSettled(promises);

    isLoaded.value = true;
  });

  return { state, tiers, isLoaded };
}
